import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useFleetData } from "./FleetDataContext";

// Base links shown to everyone; Events is added conditionally for logged-in users
const BASE_NAV_LINKS = [
  { label: "Home", href: "/", external: false },
  { label: "Hierarchy", href: "/hierarchy", external: false },
];

// Pages may pass `mobileSlot` to inject a control (typically a section dropdown)
// inline next to the logo on mobile portrait. Lives in the same flex container as
// the logo Link so spacing stays consistent without needing pixel offsets.
export default function Nav({ accentColor = "#fcd657", mobileSlot = null }) {
  const { user, profile, signInWithDiscord, signOut, isAdmin } = useAuth();
  const { teams } = useFleetData();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Resolve primary team name → team object for color + slug
  const primaryTeamName = profile?.primary_team;
  const primaryTeam = primaryTeamName ? (teams || []).find(t => t.name === primaryTeamName) : null;
  const primaryTeamColor = primaryTeam?.color || "#ce93d8";
  const primaryTeamDisplay = primaryTeam?.nav_label || primaryTeamName;
  const primaryTeamSlug = primaryTeamName ? primaryTeamName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : null;

  // Build nav links — Events and Fleet only visible to logged-in users
  const NAV_LINKS = [
    ...BASE_NAV_LINKS,
    ...(user ? [
      { label: "Fleet", href: "/fleet", external: false },
      { label: "Events", href: "/events", external: false },
      { label: "Resources", href: "/resources", external: false },
      { label: "Market", href: "/market", external: false },
    ] : []),
  ];

  return (
    <>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        /* Safe area: extend background behind status bar, push content below it */
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingLeft: "max(env(safe-area-inset-left, 0px), clamp(16px, 4vw, 40px))",
        paddingRight: "max(env(safe-area-inset-right, 0px), clamp(16px, 4vw, 40px))",
        background: "rgba(5,7,15,0.88)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: `1px solid ${accentColor}33`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: "64px",
        boxSizing: "content-box",
      }}>
        {/* Logo + optional mobile slot — sit in the same flex row so spacing
            between them is governed by the row's gap, not pixel offsets. */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 1, minWidth: 0 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <img
              src="/images/logo.png"
              alt="Starkeeper"
              style={{ height: 34, width: "auto" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <span className="nav-logo-text" style={{
              fontFamily: "'Orbitron', sans-serif", fontWeight: 700,
              fontSize: "clamp(10px, 2vw, 14px)", letterSpacing: 3, color: "#e8eaf6",
            }}>STARKEEPER</span>
          </Link>
          {mobileSlot && (
            <div className="nav-mobile-slot" style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              {mobileSlot}
            </div>
          )}
        </div>

        {/* Desktop Links */}
        <div className="nav-desktop-links" style={{ display: "flex", gap: "clamp(12px, 2vw, 28px)", alignItems: "center" }}>
          {NAV_LINKS.map((l) => (
            l.external
              ? <a key={l.label} href={l.href} className={`nav-link${l.label === "Home" ? " nav-home-link" : ""}`}>{l.label}</a>
              : <Link key={l.label} to={l.href} className={`nav-link${l.label === "Home" ? " nav-home-link" : ""}`}>{l.label}</Link>
          ))}

          {primaryTeam && (
            <Link to={`/teams/${primaryTeamSlug}`} style={{
              fontFamily: "'Orbitron', sans-serif", fontSize: 10,
              letterSpacing: 2, color: primaryTeamColor, textTransform: "uppercase",
              textShadow: `0 0 8px ${primaryTeamColor}33`,
              transition: "all 0.2s",
            }}>{primaryTeamDisplay}</Link>
          )}

          {isAdmin && (
            <Link to="/admin" style={{
              fontFamily: "'Orbitron', sans-serif", fontSize: 10,
              letterSpacing: 2, color: "#fcd657", textTransform: "uppercase",
            }}>Admin</Link>
          )}

          {/* Auth section — avatar only, sign out moved to profile */}
          {user ? (
            <Link to="/profile" title="View your profile" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <div style={{
                width: 34, height: 34, borderRadius: "50%", overflow: "hidden",
                border: `2px solid ${accentColor}99`, flexShrink: 0,
                boxShadow: `0 0 10px ${accentColor}44`,
                cursor: "pointer", transition: "all 0.2s",
              }}>
                {profile?.discord_avatar
                  ? <img src={profile.discord_avatar} alt={profile.discord_username}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                  : <div style={{
                      width: "100%", height: "100%", background: "#0d1120",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Orbitron'", fontSize: 14, color: accentColor,
                    }}>{profile?.discord_username?.charAt(0)}</div>
                }
              </div>
            </Link>
          ) : (
            <button onClick={signInWithDiscord} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 16px", background: "#5865F2", border: "none",
              color: "#fff", fontFamily: "'Orbitron', sans-serif", fontSize: 10,
              fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
              cursor: "pointer", transition: "all 0.2s", borderRadius: 2,
            }}
              onMouseEnter={e => e.currentTarget.style.background = "#4752c4"}
              onMouseLeave={e => e.currentTarget.style.background = "#5865F2"}>
              <svg width="16" height="12" viewBox="0 0 71 55" fill="white" xmlns="http://www.w3.org/2000/svg">
                <path d="M60.1 4.9A58.5 58.5 0 0 0 45.5.7a.2.2 0 0 0-.2.1 40.7 40.7 0 0 0-1.8 3.7 54 54 0 0 0-16.2 0A37.7 37.7 0 0 0 25.5.8a.2.2 0 0 0-.2-.1A58.3 58.3 0 0 0 10.7 4.9a.2.2 0 0 0-.1.1C1.5 18.2-1 31 .3 43.7a.2.2 0 0 0 .1.2 58.8 58.8 0 0 0 17.7 9 .2.2 0 0 0 .2-.1 42 42 0 0 0 3.6-5.9.2.2 0 0 0-.1-.3 38.7 38.7 0 0 1-5.5-2.6.2.2 0 0 1 0-.4l1.1-.8a.2.2 0 0 1 .2 0c11.5 5.3 24 5.3 35.4 0a.2.2 0 0 1 .2 0l1.1.8a.2.2 0 0 1 0 .4 36.1 36.1 0 0 1-5.5 2.6.2.2 0 0 0-.1.3 47.1 47.1 0 0 0 3.6 5.9.2.2 0 0 0 .2.1 58.6 58.6 0 0 0 17.8-9 .2.2 0 0 0 .1-.2C73.3 29 70 16.3 60.2 5a.2.2 0 0 0-.1-.1zM23.7 36.3c-3.5 0-6.4-3.2-6.4-7.2s2.8-7.2 6.4-7.2c3.6 0 6.5 3.3 6.4 7.2 0 4-2.8 7.2-6.4 7.2zm23.7 0c-3.5 0-6.4-3.2-6.4-7.2s2.8-7.2 6.4-7.2c3.6 0 6.5 3.3 6.4 7.2 0 4-2.8 7.2-6.4 7.2z"/>
              </svg>
              Sign In
            </button>
          )}
        </div>

        {/* Mobile hamburger button */}
        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: "none", background: "none", border: "none",
            color: "#e8eaf6", fontSize: 24, cursor: "pointer", padding: 8,
            flexShrink: 0,
          }}
          aria-label="Toggle menu"
        >
          {menuOpen ? "✕" : "☰"}
        </button>
      </nav>

      {/* Mobile slide-out menu */}
      {menuOpen && (
        <div
          style={{
            position: "fixed", top: "calc(64px + env(safe-area-inset-top, 0px))",
            left: 0, right: 0, bottom: 0,
            zIndex: 999, background: "rgba(5,7,15,0.95)",
            backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
            display: "flex", flexDirection: "column",
            padding: "24px clamp(16px, 4vw, 40px)",
            paddingLeft: "max(env(safe-area-inset-left, 0px), clamp(16px, 4vw, 40px))",
            paddingRight: "max(env(safe-area-inset-right, 0px), clamp(16px, 4vw, 40px))",
            paddingBottom: "env(safe-area-inset-bottom, 24px)",
            gap: 8, overflowY: "auto",
          }}
          className="nav-mobile-menu"
        >
          {NAV_LINKS.map((l) => (
            l.external
              ? <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)} style={{
                  fontFamily: "'Orbitron', sans-serif", fontSize: 14, letterSpacing: 3,
                  color: "#e8eaf6", textTransform: "uppercase", padding: "16px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}>{l.label}</a>
              : <Link key={l.label} to={l.href} onClick={() => setMenuOpen(false)} style={{
                  fontFamily: "'Orbitron', sans-serif", fontSize: 14, letterSpacing: 3,
                  color: "#e8eaf6", textTransform: "uppercase", padding: "16px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.06)", textDecoration: "none",
                }}>{l.label}</Link>
          ))}

          {primaryTeam && (
            <Link to={`/teams/${primaryTeamSlug}`} onClick={() => setMenuOpen(false)} style={{
              fontFamily: "'Orbitron', sans-serif", fontSize: 14, letterSpacing: 3,
              color: primaryTeamColor, textTransform: "uppercase", padding: "16px 0",
              borderBottom: "1px solid rgba(255,255,255,0.06)", textDecoration: "none",
              textShadow: `0 0 8px ${primaryTeamColor}33`,
            }}>{primaryTeamDisplay}</Link>
          )}

          {isAdmin && (
            <Link to="/admin" onClick={() => setMenuOpen(false)} style={{
              fontFamily: "'Orbitron', sans-serif", fontSize: 14, letterSpacing: 3,
              color: "#fcd657", textTransform: "uppercase", padding: "16px 0",
              borderBottom: "1px solid rgba(255,255,255,0.06)", textDecoration: "none",
            }}>Admin</Link>
          )}

          <div style={{ marginTop: 16 }}>
            {user ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Link to="/profile" onClick={() => setMenuOpen(false)} style={{
                  display: "flex", alignItems: "center", gap: 12, textDecoration: "none",
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: "50%", overflow: "hidden",
                    border: `2px solid ${accentColor}99`, flexShrink: 0,
                  }}>
                    {profile?.discord_avatar
                      ? <img src={profile.discord_avatar} alt={profile.discord_username}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                      : <div style={{
                          width: "100%", height: "100%", background: "#0d1120",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontFamily: "'Orbitron'", fontSize: 16, color: accentColor,
                        }}>{profile?.discord_username?.charAt(0)}</div>
                    }
                  </div>
                  <span style={{
                    fontFamily: "'Rajdhani', sans-serif", fontSize: 16,
                    color: "rgba(232,234,246,0.85)",
                  }}>{profile?.discord_username}</span>
                </Link>
                <button onClick={() => { signOut(); setMenuOpen(false); }} style={{
                  fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2,
                  color: "rgba(232,234,246,0.5)", textTransform: "uppercase",
                  background: "none", border: "1px solid rgba(255,255,255,0.12)",
                  padding: "10px 20px", cursor: "pointer", textAlign: "center",
                }}>
                  Sign Out
                </button>
              </div>
            ) : (
              <button onClick={() => { signInWithDiscord(); setMenuOpen(false); }} style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                width: "100%", padding: "14px 20px", background: "#5865F2", border: "none",
                color: "#fff", fontFamily: "'Orbitron', sans-serif", fontSize: 12,
                fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
                cursor: "pointer", borderRadius: 2,
              }}>
                Sign In with Discord
              </button>
            )}
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .nav-desktop-links { display: none !important; }
          .nav-hamburger { display: block !important; }
        }
        @media (min-width: 769px) {
          .nav-mobile-menu { display: none !important; }
          .nav-mobile-slot { display: none !important; }
        }
        @media (min-width: 769px) and (max-width: 1100px) {
          .nav-logo-text { display: none !important; }
          .nav-home-link { display: none !important; }
        }
      `}</style>
    </>
  );
}
